import Link from 'next/link'
import Image from 'shared/components/Image'
import { colors, media } from 'shared/lib'
import {
  ArticleProductTemplate,
  ArticleProductTemplateColumns,
} from 'shared/types/ArticleProductTemplateColumns'
import styled, { css } from 'styled-components'

type ProductTemplateProps = {
  columns: number
  product: ArticleProductTemplate
}

const ProductTemplate = ({ columns, product }: ProductTemplateProps) => {
  return (
    <StyledLink data-qa="ArticleProductTemplateLink" href={`/${product?.slug}`}>
      <PtWrapper>
        <PtImageContainer $columns={columns}>
          <Image
            alt={product?.name}
            src={product?.thumbUrl}
            data-qa="ArticleProductTemplateImage"
            sizes="(max-width: 768px) 100vw, 50vw"
            width={704}
            height={502}
          />
        </PtImageContainer>
        <PtName data-qa="ArticleProductTemplateName">{product?.name}</PtName>
      </PtWrapper>
    </StyledLink>
  )
}

type ArticleProductTemplateColumnsSectionProps = {
  section: ArticleProductTemplateColumns
}

export const ArticleProductTemplateColumnsSection = ({
  section,
}: ArticleProductTemplateColumnsSectionProps) => {
  if (!section?.productTemplates || section?.productTemplates?.length === 0) {
    return null
  }

  return (
    <Container>
      <PtContainer $columns={section?.columns} data-qa="ArticleProductTemplateContainer">
        {section?.productTemplates?.map((pt) => (
          <ProductTemplate columns={section?.columns} key={pt?.id} product={pt} />
        ))}
      </PtContainer>
    </Container>
  )
}

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 120px;
  margin-top: 120px;
  width: 100vw;

  ${media.medium`
    margin-top: 240px;
  `};
`
const PtContainer = styled.div<{ $columns: number }>`
  display: grid;
  gap: 24px;
  grid-template-rows: 1fr;
  place-items: center;
  width: 100vw;

  ${({ $columns }) => {
    if ($columns === 1) {
      return css`
        grid-template-columns: 1fr;
        padding: 0 24px;
      `
    }

    if ($columns === 2) {
      return css`
        grid-template-columns: 1fr 1fr;
        padding: 0 24px;

        ${media.medium`
          width: 1026px;
        `}
      `
    }

    return css`
      grid-template-columns: 1fr 1fr;
      padding: 0 24px;

      ${media.medium`
        grid-template-columns: 1fr 1fr 1fr;
        padding: 0 40px;
      `}
    `
  }}
`
const PtImageContainer = styled.div<{ $columns: number }>`
  img {
    height: fit-content;

    ${({ $columns }) =>
      $columns === 1
        ? css`
            ${media.medium`
              width: 704px;
            `}
          `
        : css`
            ${media.medium`
              width: 461px;
            `}

            ${media.biggest`
              width: auto;
              max-width: 597px;
            `}
          `}
  }
`
const PtName = styled.div`
  color: ${colors.FC2_RED};
  font-family: 'Helvetica Neue'; /* stylelint-disable-line font-family-no-missing-generic-family-keyword */
  font-size: 12px;
  font-weight: 400;
  line-height: 125%;
  text-align: center;
  text-transform: uppercase;
  text-wrap: balance;
`
const PtWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
`
const StyledLink = styled(Link)`
  text-decoration: none;
`
